import { getManufacturerDefaults } from '../../../../helpers/getManufacturerDefaults';
import { isValidInterval } from '../../../../helpers/isValidInterval';
import { useEffect, useState } from 'react';

import type {
    IntervalUnit,
    PartType,
    ServiceIntervalMap,
    UseIntervalDraftParams,
    UseIntervalDraftReturn
} from './types';

const useIntervalDraft = ({
    isOpen,
    model,
    serviceIntervals
}: UseIntervalDraftParams): UseIntervalDraftReturn => {
    const [draftIntervals, setDraftIntervals] = useState<ServiceIntervalMap>(serviceIntervals);

    // Reseed the draft whenever the panel opens or the selected model changes, so
    // switching models always shows that model's saved intervals (never a stale draft).
    useEffect(() => {
        if (isOpen) {
            setDraftIntervals(serviceIntervals);
        }
    }, [isOpen, model, serviceIntervals]);

    const handleChangeDraft = ({ part, value }: { part: PartType; value: number }) =>
        setDraftIntervals(current => {
            const previous = current[part];

            if (!previous) {
                return current;
            }

            return { ...current, [part]: { ...previous, value } };
        });

    const handleChangeDraftUnit = ({ part, unit }: { part: PartType; unit: IntervalUnit }) =>
        setDraftIntervals(current => {
            const previous = current[part];

            if (!previous) {
                return current;
            }

            return { ...current, [part]: { ...previous, unit } };
        });

    const handleResetDraft = ({ part }: { part: PartType }) =>
        setDraftIntervals(current => {
            const oem = getManufacturerDefaults(model)[part];

            if (!oem) {
                return current;
            }

            return { ...current, [part]: { ...oem } };
        });

    const draftParts = Object.keys(draftIntervals) as PartType[];

    const isDraftValid = draftParts.every(part => {
        const config = draftIntervals[part];

        return !config || isValidInterval({ unit: config.unit, value: config.value });
    });

    return {
        draftIntervals,
        handleChangeDraft,
        handleChangeDraftUnit,
        handleResetDraft,
        isDraftValid
    };
};

export default useIntervalDraft;
